<template>
  <div :style="{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }">
    <!-- Top bar -->
    <header :style="{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: '16px', padding: '12px 18px', borderBottom: '1px solid var(--p-content-border-color)' }">
      <template v-if="effectiveSearchUi === 'bar'">
        <div :style="{ flex: 1, minWidth: 0 }">
          <SearchBar
            :search="search"
            :field-definitions="fd"
            :field-meta="fieldMeta"
            :value-provider="valueProvider"
            :known-values="knownValues"
            :debounce-ms="debounceMs"
            :placeholder="config.searchPlaceholder || 'Search…'"
            :help-text="config.searchHelp || 'Search by field — or use Filters below.'"
            :total-count="config.total ?? null"
          >
            <template v-if="!isSmall" #topRight>
              <ViewControls
                :on-default="onDefault"
                :active-view="activeView"
                :is-dirty="isDirty"
                @update="updateActiveView"
                @save-as="openSaveAs"
                @revert="recallView(activeView)"
              />
            </template>
            <template #bottomRight>
              <CollectionControls
                :config="config"
                :field-meta="fieldMeta"
                :view-type="viewType"
                :density="density"
                :table-cols="tableCols"
                :allowed-views="allowedViews"
                @update:density="density = $event"
                @update:view-type="viewType = $event"
                @update:table-cols="tableCols = $event"
              />
            </template>
          </SearchBar>
        </div>
      </template>
      <template v-else-if="effectiveSearchUi === 'summary'">
        <div :style="{ flex: 1, minWidth: 0 }">
          <SearchSummary :search="search" :field-meta="fieldMeta" :kind-label="config.kindLabel" @clear="clearAllFilters" />
        </div>
        <div :style="{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }">
          <ViewControls
            v-if="!isSmall"
            :on-default="onDefault"
            :active-view="activeView"
            :is-dirty="isDirty"
            @update="updateActiveView"
            @save-as="openSaveAs"
            @revert="recallView(activeView)"
          />
          <CollectionControls
            :config="config"
            :field-meta="fieldMeta"
            :view-type="viewType"
            :density="density"
            :table-cols="tableCols"
            :allowed-views="allowedViews"
            @update:density="density = $event"
            @update:view-type="viewType = $event"
            @update:table-cols="tableCols = $event"
          />
        </div>
      </template>
      <template v-else>
        <div :style="{ flex: 1, minWidth: 0 }" />
        <div :style="{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }">
          <ViewControls
            v-if="!isSmall"
            :on-default="onDefault"
            :active-view="activeView"
            :is-dirty="isDirty"
            @update="updateActiveView"
            @save-as="openSaveAs"
            @revert="recallView(activeView)"
          />
          <CollectionControls
            :config="config"
            :field-meta="fieldMeta"
            :view-type="viewType"
            :density="density"
            :table-cols="tableCols"
            :allowed-views="allowedViews"
            @update:density="density = $event"
            @update:view-type="viewType = $event"
            @update:table-cols="tableCols = $event"
          />
        </div>
      </template>
    </header>

    <!-- Work area -->
    <div
      ref="workRef"
      :style="{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden', position: 'relative', cursor: dragging ? 'col-resize' : 'default', userSelect: dragging ? 'none' : 'auto' }"
    >
      <LeftPane
        v-if="!isSmall"
        :open="leftOpen"
        :tab="leftTab"
        :tab-badges="{ filter: filterBadge }"
        @tab="leftTab = $event"
        @toggle="leftOpen = $event"
      >
        <div :style="{ flex: 1, minHeight: 0, display: leftTab === 'filter' ? 'flex' : 'none' }">
          <SearchSidebar
            :search="search"
            :field-definitions="fd"
            :field-meta="fieldMeta"
            :value-provider="valueProvider"
            :facet-provider="facetProvider"
            :known-values="knownValues"
            :debounce-ms="debounceMs"
            :embedded="true"
          />
        </div>
        <div :style="{ flex: 1, minHeight: 0, display: leftTab === 'views' ? 'flex' : 'none', flexDirection: 'column' }">
          <SavedViews
            :data="data"
            :field-definitions="fd"
            :views="views"
            :active-view-id="activeViewId"
            :is-dirty="isDirty"
            :can-save="canSaveCurrent || isDirty"
            :saving-as="savingAs"
            :recall-view="recallView"
            :save-as-new="saveAsNew"
            :delete-view="deleteView"
            @update:saving-as="savingAs = $event"
          />
        </div>
      </LeftPane>

      <!-- Center -->
      <main :style="{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }">
        <div :style="{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', padding: '8px 18px 6px' }">
          <span :style="{ fontSize: '11px', fontVariantNumeric: 'tabular-nums', color: 'var(--p-surface-400)', letterSpacing: '.01em' }">
            {{ countText }}
          </span>
          <button
            v-if="!config.hideNew"
            :title="`New ${config.kindLabel || 'record'}`"
            :style="{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: '30px', padding: '0 12px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, border: '1px solid var(--p-content-border-color)', background: 'transparent', color: 'var(--p-text-muted-color)' }"
            @click="createRecord"
          >
            <i class="pi pi-plus" :style="{ fontSize: '11px' }" />New
          </button>
        </div>
        <div :style="{ flex: 1, minHeight: 0, overflowY: 'auto' }">
          <CollectionBody
            :view-type="viewType"
            :density="density"
            :items="sortedResults"
            :id-key="idKey"
            :config="config"
            :field-definitions="fd"
            :field-meta="fieldMeta"
            :cols="visibleFields"
            :open-id="openId"
            :render-list-row="config.renderListRow"
            :render-card="config.renderCard"
            :render-grid-tile="config.renderGridTile"
            :render-empty="config.renderEmpty"
            :grid-min-width="config.gridMinWidth"
            :compact-padded="config.compactPadded"
            :col-config="tableCols"
            @open="openRow"
            @open-expanded="openExpanded"
            @cycle-sort="cycleSort"
          />
        </div>
      </main>

      <!-- Right instance panel (side) -->
      <template v-if="openRecord && panelMode === 'side'">
        <div
          title="Drag to resize · double-click to reset"
          :style="{ flexShrink: 0, width: '7px', marginLeft: '-3px', marginRight: '-3px', zIndex: 5, cursor: 'col-resize', display: 'flex', justifyContent: 'center', alignItems: 'stretch' }"
          @mousedown="startSplitDrag"
          @touchstart="startSplitDrag"
          @dblclick="panelFrac = DEFAULT_PANEL_FRAC"
        >
          <span :style="{ width: dragging ? '2px' : '1px', background: dragging ? 'var(--p-primary-500)' : 'var(--p-content-border-color)', transition: dragging ? 'none' : 'background .12s' }" />
        </div>
        <InstancePanel
          :record="openRecord"
          :config="config"
          :field-definitions="fd"
          :field-meta="fieldMeta"
          mode="side"
          :side-width="sidePanelWidth"
          :is-new="isNewOpen"
          @mode-change="panelMode = $event"
          @close="onPanelClose"
          :commit="saveRecord"
          @delete="(id, opts) => deleteRecord(id, opts)"
          @open-new-tab="openInNewTab(openId)"
          @request-edit="onRequestEdit"
        >
          <template v-if="slots.instance" #instance="ctx"><slot name="instance" v-bind="ctx" /></template>
        </InstancePanel>
      </template>
    </div>

    <!-- Right instance panel (expanded overlay) -->
    <InstancePanel
      v-if="openRecord && panelMode === 'expanded'"
      :record="openRecord"
      :config="config"
      :field-definitions="fd"
      :field-meta="fieldMeta"
      mode="expanded"
      :is-new="isNewOpen"
      :start-editing="editIntent"
      @mode-change="panelMode = $event"
      @close="onPanelClose"
      :commit="saveRecord"
      @delete="(id, opts) => deleteRecord(id, opts)"
    >
      <template v-if="slots.instance" #instance="ctx"><slot name="instance" v-bind="ctx" /></template>
    </InstancePanel>
  </div>
</template>

<script setup>
// ── ListEditShell — structured-search list & edit workspace ──────────────
// Composes SearchBar/SearchSummary + SearchSidebar + CollectionBody + the
// right-hand InstancePanel. Entity-agnostic: driven by `config`. Everything
// (query, view type, left-pane state, open record, saved views, edits) persists
// in localStorage. Ported 1:1 from list-edit-shell.jsx. See PROMPT §4.4 for the
// panel lifecycle and the editIntent bridge.
import { ref, computed, watch, onMounted, onBeforeUnmount, provide, inject, useSlots } from 'vue'
import { useStructuredSearch } from '@/admin-collection-layout/composables/useStructuredSearch.js'
import { sortByConfig, withSort } from '@/admin-collection-layout/lib/collectionHelpers.js'
import SearchBar from './SearchBar.vue'
import SearchSidebar from './SearchSidebar.vue'
import InstancePanel from './InstancePanel.vue'
import CollectionBody from './collection/CollectionBody.vue'
import LeftPane from './shell/LeftPane.vue'
import SavedViews from './shell/SavedViews.vue'
import SearchSummary from './shell/SearchSummary.vue'
import CollectionControls from './shell/CollectionControls.vue'
import ViewControls from './shell/ViewControls.vue'

const props = defineProps({
  config: { type: Object, required: true },
  valueProvider: { type: Function, default: null },
  facetProvider: { type: Function, default: null },
  knownValues: { type: Object, default: null },
  debounceMs: { type: Number, default: 220 },
  searchUi: { type: String, default: 'bar' },
  simplifyWhenSmall: { type: Boolean, default: true },
  smallThreshold: { type: Number, default: 25 },
  // Controlled mode: the host owns the data (e.g. a server-driven list). The
  // localStorage created/deleted/edits overlays are bypassed; create/save/delete
  // call config.onCreate/onSave/onDelete and the host is expected to refresh
  // config.data. A brand-new record is held transiently (not persisted) until
  // its first save. See PROMPT integration plan.
  controlled: { type: Boolean, default: false },
  // A query to open on, ahead of the one localStorage remembers. It is how a
  // link carries a filter: a roster entry on one screen points at another
  // screen already narrowed to what it named. Empty means "whatever was
  // persisted", which is every ordinary visit.
  initialQuery: { type: String, default: '' },
})
const slots = useSlots()

// localStorage helper scoped to a key prefix.
function makeStore(prefix) {
  const k = (s) => `les:${prefix}:${s}`
  return {
    get(s, fallback) {
      try {
        const v = localStorage.getItem(k(s))
        return v == null ? fallback : JSON.parse(v)
      } catch {
        return fallback
      }
    },
    set(s, v) {
      try {
        localStorage.setItem(k(s), JSON.stringify(v))
      } catch {
        /* ignore */
      }
    },
  }
}
// Structural equality for a saved-view bundle.
function sameBundle(a, b) {
  if (!a || !b) return false
  if ((a.query || '') !== (b.query || '')) return false
  if ((a.viewType || '') !== (b.viewType || '')) return false
  if ((a.density || '') !== (b.density || '')) return false
  const fa = a.fields
  const fb = b.fields
  if (!Array.isArray(fa) || !Array.isArray(fb)) return false
  if (fa.length !== fb.length) return false
  for (let i = 0; i < fa.length; i++) {
    if (fa[i].key !== fb[i].key || !!fa[i].visible !== !!fb[i].visible || (fa[i].sort || null) !== (fb[i].sort || null)) return false
  }
  return true
}
function nowMs() {
  return Date.now()
}

const config = computed(() => props.config)
const store = makeStore(props.config.storageKey || 'default')
const viewsStore = makeStore(props.config.viewsKey || props.config.storageKey || 'default')
const idKey = props.config.idKey || 'id'
const fd = props.config.fieldDefinitions
const fieldMeta = props.config.fieldMeta || {}

// ── persisted UI state ───────────────────────────────────────────────
const leftOpen = ref(store.get('leftOpen', true))
const leftTab = ref(store.get('leftTab', 'filter'))
const allowedViews = props.config.viewTypes && props.config.viewTypes.length ? props.config.viewTypes : null
const viewType = ref(
  (() => {
    let vt = store.get('viewType', 'list')
    vt = vt === 'card' ? 'list' : vt
    if (allowedViews && !allowedViews.includes(vt)) vt = allowedViews[0]
    return vt
  })(),
)
const density = ref(store.get('density', 'expanded'))
const openId = ref(store.get('openId', null))
const panelMode = ref(store.get('panelMode', props.config.defaultPanelMode || 'side'))
const newId = ref(null)

// editIntent bridges the side overview's Edit → the expanded panel mount.
const editIntent = ref(false)
watch(openId, () => {
  editIntent.value = false
})
watch([editIntent, panelMode], () => {
  if (editIntent.value && panelMode.value === 'expanded') {
    const t = setTimeout(() => {
      editIntent.value = false
    }, 0)
    return () => clearTimeout(t)
  }
})

// ── breadcrumb bridge (optional; default no-op) ───────────────────────
const parentRegisterCrumb = inject('registerCrumb', () => {})
const emit = defineEmits(['crumb', 'update:query', 'update:sort'])
function registerCrumb(labels) {
  let arr = labels
  if (labels && labels.length) {
    arr = labels.map((c, i) => {
      if (i !== labels.length - 1) return c
      const base = typeof c === 'string' ? { label: c } : { ...c }
      return {
        ...base,
        onClose: () => {
          openId.value = null
          newId.value = null
        },
      }
    })
  }
  parentRegisterCrumb(arr)
  emit('crumb', arr)
}
provide('registerCrumb', registerCrumb)

// ── split-pane width ──────────────────────────────────────────────────
const DEFAULT_PANEL_FRAC = 0.32
const panelFrac = ref(store.get('panelFrac', DEFAULT_PANEL_FRAC))
watch(panelFrac, (v) => store.set('panelFrac', v))
const workRef = ref(null)
const dragging = ref(false)
function startSplitDrag(e) {
  e.preventDefault()
  const el = workRef.value
  if (!el) return
  dragging.value = true
  const onMove = (ev) => {
    const r = el.getBoundingClientRect()
    const x = ev.touches ? ev.touches[0].clientX : ev.clientX
    let w = r.right - x
    const min = 360
    const max = Math.max(min + 40, r.width * 0.6)
    w = Math.max(min, Math.min(max, w))
    panelFrac.value = w / r.width
  }
  const onUp = () => {
    dragging.value = false
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onUp)
    window.removeEventListener('touchmove', onMove)
    window.removeEventListener('touchend', onUp)
  }
  window.addEventListener('mousemove', onMove)
  window.addEventListener('mouseup', onUp)
  window.addEventListener('touchmove', onMove, { passive: false })
  window.addEventListener('touchend', onUp)
}
const workW = ref(1200)
let resizeObserver = null
onMounted(() => {
  const el = workRef.value
  if (!el || !window.ResizeObserver) return
  resizeObserver = new ResizeObserver((entries) => {
    workW.value = entries[0].contentRect.width
  })
  resizeObserver.observe(el)
})
onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect()
})
const sidePanelWidth = computed(() => Math.round(Math.max(360, Math.min(workW.value * 0.6, workW.value * panelFrac.value))))

// ── table column config ───────────────────────────────────────────────
// Renamed from `tableCols` when each collection gained its own default
// columns, so a layout saved against the old shared defaults is set aside once
// rather than hiding the new ones.
const COLUMN_LAYOUT_KEY = 'columnLayout'
const allCols = computed(() => ['__primary', ...Object.keys(props.config.fieldDefinitions.fields || {})])
const tableCols = ref(
  (() => {
    const known = new Set(allCols.value)
    const saved = viewsStore.get(COLUMN_LAYOUT_KEY, null)
    if (Array.isArray(saved)) {
      const kept = saved.filter((c) => known.has(c.key))
      const have = new Set(kept.map((c) => c.key))
      allCols.value.forEach((k) => {
        if (!have.has(k)) kept.push({ key: k, visible: false, sort: null })
      })
      return kept
    }
    const def = (props.config.tableColumns || ['__primary']).filter((k) => known.has(k))
    const visSet = new Set(def)
    const order = [...def]
    allCols.value.forEach((k) => {
      if (!order.includes(k)) order.push(k)
    })
    return order.map((k) => ({ key: k, visible: visSet.has(k), sort: null }))
  })(),
)

// ── overlays + saved views ────────────────────────────────────────────
const edits = ref(store.get('edits', {}))
const views = ref(
  (() => {
    const saved = viewsStore.get('views', [])
    if (saved.some((v) => v.isDefault)) return saved
    const vt0 = store.get('viewType', 'list')
    const def = {
      id: 'default',
      name: 'Default',
      isDefault: true,
      query: store.get('query', ''),
      viewType: vt0 === 'card' ? 'list' : vt0,
      density: store.get('density', 'expanded'),
      fields: null,
      createdAt: nowMs(),
    }
    return [def, ...saved.map((v) => ({ ...v, isDefault: false }))]
  })(),
)
const created = ref(store.get('created', []))
const deleted = ref(store.get('deleted', []))

watch(created, (v) => store.set('created', v), { deep: true })
watch(deleted, (v) => store.set('deleted', v), { deep: true })
watch(tableCols, (v) => viewsStore.set(COLUMN_LAYOUT_KEY, v), { deep: true })
watch(views, (v) => viewsStore.set('views', v), { deep: true })
watch(leftOpen, (v) => store.set('leftOpen', v))
watch(leftTab, (v) => store.set('leftTab', v))
watch(viewType, (v) => store.set('viewType', v))
watch(density, (v) => store.set('density', v))
watch(openId, (v) => store.set('openId', v))
watch(panelMode, (v) => store.set('panelMode', v))
watch(edits, (v) => store.set('edits', v), { deep: true })

// ── effective data ────────────────────────────────────────────────────
// In controlled mode the host owns config.data; we only layer in a transient
// (unsaved) new record. Otherwise we apply the localStorage overlays.
const transientNew = ref(null)
const delSet = computed(() => new Set(deleted.value))
const data = computed(() => {
  if (props.controlled) {
    return [...(transientNew.value ? [transientNew.value] : []), ...props.config.data]
  }
  return [...created.value, ...props.config.data]
    .filter((r) => !delSet.value.has(r[idKey]))
    .map((r) => (edits.value[r[idKey]] ? { ...r, ...edits.value[r[idKey]] } : r))
})

function createRecord() {
  const base = props.config.newRecord ? props.config.newRecord() : {}
  const id = base[idKey] != null ? base[idKey] : 'new-' + nowMs()
  const rec = { ...base, [idKey]: id }
  if (props.controlled) {
    // Hold the new record transiently; persist on first save (→ onCreate).
    transientNew.value = rec
  } else {
    created.value = [rec, ...created.value]
    if (props.config.onCreate) props.config.onCreate(rec)
  }
  newId.value = id
  openId.value = id
  panelMode.value = 'expanded'
}
function deleteRecord(id, opts) {
  if (props.controlled) {
    if (transientNew.value && String(transientNew.value[idKey]) === String(id)) {
      transientNew.value = null // cancel an unsaved new record
    } else if (props.config.onDelete && !(opts && opts.silent)) {
      props.config.onDelete(id)
    }
    if (String(openId.value) === String(id)) {
      openId.value = null
      newId.value = null
    }
    return
  }
  if (created.value.some((r) => r[idKey] === id)) created.value = created.value.filter((r) => r[idKey] !== id)
  else if (!deleted.value.includes(id)) deleted.value = [...deleted.value, id]
  if (edits.value[id]) {
    const n = { ...edits.value }
    delete n[id]
    edits.value = n
  }
  if (props.config.onDelete && !(opts && opts.silent)) props.config.onDelete(id)
  if (String(openId.value) === String(id)) {
    openId.value = null
    newId.value = null
  }
}

// ── structured search ─────────────────────────────────────────────────
// The prop wins where there is one. A link that names a filter is a request
// made now; the persisted query is what this screen was left showing, and it
// stays the answer on every visit that asks for nothing.
const search = useStructuredSearch({
  initialQuery: props.initialQuery || store.get('query', ''),
  fieldDefinitions: props.config.fieldDefinitions,
  data,
})
watch(
  () => search.query,
  (v) => {
    store.set('query', v)
    emit('update:query', v)
  },
)
// Surface the initial (persisted) query so a server-driven host fetches for it.
onMounted(() => emit('update:query', search.query))

const openRecord = computed(() => (openId.value == null ? null : data.value.find((r) => String(r[idKey]) === String(openId.value)) || null))
// Answers whether the write stuck, so the panel knows whether to leave edit
// mode. In controlled mode the host talks to a server that can refuse, and the
// old code cleared `transientNew` and `newId` before the answer arrived — so a
// refused create discarded everything somebody had typed and closed the panel.
async function saveRecord(next) {
  const isNew = String(next[idKey]) === String(newId.value)
  if (props.controlled) {
    if (isNew) {
      const ok = props.config.onCreate ? await props.config.onCreate(next) : true
      if (ok === false) return false
      transientNew.value = null
      newId.value = null
      return true
    }
    if (props.config.onSave) {
      const ok = await props.config.onSave(next)
      if (ok === false) return false
    }
    return true
  }
  edits.value = { ...edits.value, [next[idKey]]: next }
  if (props.config.onSave) props.config.onSave(next)
  if (isNew) newId.value = null
  return true
}

// ── deep link ─────────────────────────────────────────────────────────
const deepLinkKey = (props.config.storageKey || 'les') + '_rec'
onMounted(() => {
  try {
    const id = new URLSearchParams(window.location.search).get(deepLinkKey)
    if (id && props.config.data.some((r) => String(r[idKey]) === String(id))) {
      openId.value = id
      panelMode.value = 'expanded'
    }
  } catch {
    /* ignore */
  }
})
function openInNewTab(id) {
  if (id == null) return
  try {
    const url = new URL(window.location.href)
    url.searchParams.set(deepLinkKey, String(id))
    window.open(url.toString(), '_blank', 'noopener')
  } catch {
    /* ignore */
  }
}

// ── sticky saved-view selection ───────────────────────────────────────
const activeViewId = ref(viewsStore.get('activeViewId', 'default') || 'default')
watch(activeViewId, (v) => viewsStore.set('activeViewId', v))
const savingAs = ref(false)
function openSaveAs() {
  leftTab.value = 'views'
  leftOpen.value = true
  savingAs.value = true
}
const curQuery = computed(() => (search.query || '').trim())
const defaultView = computed(() => views.value.find((v) => v.isDefault) || null)
const activeView = computed(() => views.value.find((v) => v.id === activeViewId.value) || defaultView.value)
const onDefault = computed(() => !!activeView.value && activeView.value.isDefault)

function reconcileFields(saved) {
  const known = new Set(allCols.value)
  const kept = (Array.isArray(saved) ? saved : [])
    .filter((c) => known.has(c.key))
    .map((c) => ({ key: c.key, visible: !!c.visible, sort: c.sort || null }))
  const have = new Set(kept.map((c) => c.key))
  allCols.value.forEach((k) => {
    if (!have.has(k)) kept.push({ key: k, visible: false, sort: null })
  })
  return kept
}

const currentBundle = computed(() => ({ query: curQuery.value, viewType: viewType.value, density: density.value, fields: tableCols.value }))

// Default view auto-saves; named views never do.
watch(
  [currentBundle, activeView],
  () => {
    const av = activeView.value
    if (!av || !av.isDefault) return
    if (sameBundle(av, currentBundle.value)) return
    views.value = views.value.map((v) => (v.isDefault ? { ...v, ...currentBundle.value, updatedAt: nowMs() } : v))
  },
  { immediate: true },
)

const isDirty = computed(() => !!activeView.value && !activeView.value.isDefault && !sameBundle(activeView.value, currentBundle.value))
const canSaveCurrent = computed(() => onDefault.value && !(search.diagnostics || []).some((d) => d.severity === 'error'))

function applyBundle(v) {
  search.setQuery(v.query || '')
  if (v.viewType) viewType.value = v.viewType === 'card' ? 'list' : v.viewType
  if (v.density) density.value = v.density
  if (Array.isArray(v.fields)) tableCols.value = reconcileFields(v.fields)
}
function recallView(v) {
  activeViewId.value = v.id
  applyBundle(v)
}
function updateActiveView() {
  const av = activeView.value
  if (!av || av.isDefault) return
  views.value = views.value.map((v) => (v.id === av.id ? { ...v, ...currentBundle.value, updatedAt: nowMs() } : v))
}
function saveAsNew(label) {
  const id = 'v' + nowMs()
  const defs = views.value.filter((v) => v.isDefault)
  const others = views.value.filter((v) => !v.isDefault)
  views.value = [...defs, { id, name: label || `View ${others.length + 1}`, isDefault: false, ...currentBundle.value, createdAt: nowMs() }, ...others]
  activeViewId.value = id
  return id
}
function deleteView(id) {
  const v = views.value.find((x) => x.id === id)
  if (!v || v.isDefault) return
  views.value = views.value.filter((x) => x.id !== id)
  if (id === activeViewId.value) {
    activeViewId.value = 'default'
    if (defaultView.value) applyBundle(defaultView.value)
  }
}

// ── list + open behavior ──────────────────────────────────────────────
const visibleFields = computed(() => tableCols.value.filter((c) => c.visible && c.key !== '__primary').map((c) => c.key))
function openRow(id) {
  if (String(openId.value) === String(id)) panelMode.value = 'expanded'
  else {
    openId.value = id
    panelMode.value = 'side'
  }
}
function openExpanded(id) {
  openId.value = id
  panelMode.value = 'expanded'
}

// A small collection auto-simplifies (hides search + filter rail) ONLY while no
// query/filter is active. The filter sidebar mutates the same FQL query string,
// so search.query covers both free-text and structured filters — this keeps the
// search UI visible whenever a query is what narrowed the list (critical for
// server-driven/controlled lists, where data is the filtered result).
const queryActive = computed(() => !!(search.query && String(search.query).trim()))
const isSmall = computed(
  () => props.simplifyWhenSmall && !queryActive.value && props.config.data.length <= props.smallThreshold,
)
const effectiveSearchUi = computed(() => (isSmall.value ? 'hidden' : props.searchUi))
function clearAllFilters() {
  search.setQuery('')
}
function cycleSort(key) {
  tableCols.value = withSort(tableCols.value, key)
}

// The sort a paged host has to ask its server for: a page sorted here would be
// fifty rows in order, not the collection. Watched as a string so hiding or
// moving a column, which rebuilds the column list, does not re-ask.
const sortSpec = computed(() => {
  const c = tableCols.value.find((each) => each.sort)
  return c ? `${c.key}:${c.sort}` : ''
})
watch(
  sortSpec,
  (spec) => {
    const [key, direction] = spec.split(':')
    emit('update:sort', spec === '' ? null : { key, direction })
  },
  { immediate: true },
)

const sortedResults = computed(() => (props.controlled ? search.results : sortByConfig(search.results, tableCols.value, props.config, fd)))
const filterBadge = computed(() => {
  const fv = search.fieldView || {}
  const bf = fv.byField || {}
  return Object.values(bf).reduce((n, s) => n + ((s && s.values && s.values.size) || 0) + (s && s.range ? 1 : 0), 0) + ((fv.advanced && fv.advanced.length) || 0)
})

const matchCount = computed(() => search.matchCount)
const kindLower = computed(() => (props.config.kindLabel || 'records').toLowerCase())
// A paged host says which slice of how many is on screen: "50 of 50" read as
// the whole collection when it was one page of it.
const countText = computed(() => {
  const held = props.config.data.length
  const total = props.config.total
  if (total == null) return isSmall.value ? `${held} ${kindLower.value}` : `${matchCount.value} of ${held} ${kindLower.value}`
  if (held === 0 || isSmall.value) return `${total} ${kindLower.value}`
  const first = (props.config.offset || 0) + 1
  return `Showing ${first}–${first + held - 1} of ${total} ${kindLower.value}`
})
const isNewOpen = computed(() => String(openId.value) === String(newId.value))

function onPanelClose() {
  openId.value = null
  editIntent.value = false
}
function onRequestEdit() {
  editIntent.value = true
  panelMode.value = 'expanded'
}
</script>
